import type {
  PmcPerformanceRedListParams,
  PmcPerformanceRedPmcPerformancePageProps,
  PmcPerformanceRedSortKey,
} from '../types/pages'
/**
 * PMC Performance Report — Angular Red/Green PMC performance reports.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ROUTE_PATTERNS } from '@/config/routes'
import { useUserRole } from '@/hooks/useUserRole'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { showApiError } from '@/lib/utils/notifications'
import { cn } from '@lib/utils/helpers'
import {
  ReportDataTable,
  type ReportDataTableColumn,
} from '../components/ReportDataTable'
import reportStyles from '../styles/reportsTable.module.css'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'
import {
  PmcPerformanceRedFilters,
  type PmcPerformanceRedFilterState,
} from '../components/PmcPerformanceRedFilters'
import { getPmcPerformanceList, getSalesTeamsForPmcReport } from '../services/reportsService'
import type { PmcPerformanceRow, SalesTeamOption } from '../types/api'
import {
  buildPmcPerformanceExportParams,
  buildReportExportUrl,
} from '../utils/reportExportUrls'
import { appendPmcPerformanceListParams } from '../utils/reportQueryParams'
import { getRolling52WeekRange } from '../utils/reportRolling52Weeks'
import {
  buildPmcPerformanceSessionFromParams,
  clearPmcPerformanceSession,
  readPmcPerformanceSession,
  sessionFilterToPmcPerformancePatch,
  writePmcPerformanceSession,
} from '../utils/pmcPerformanceSessionFilter'
import {
  DEFAULT_FILTER,
  DEFAULT_PARAMS,
  yearStart,
} from '../constants/pages/pmcPerformanceRedPage'

function initialParams(): PmcPerformanceRedListParams {
  const session = readPmcPerformanceSession()
  if (session) {
    return { ...DEFAULT_PARAMS, ...sessionFilterToPmcPerformancePatch(session) }
  }
  return { ...DEFAULT_PARAMS }
}

function countActivePmcFilters(f: PmcPerformanceRedFilterState): number {
  let n = 0
  if (f.sales_team.length) n++
  if (f.company_id) n++
  if (f.fromDt !== DEFAULT_FILTER.fromDt || f.toDt !== DEFAULT_FILTER.toDt) n++
  if (f.isRolling) n++
  return n
}

function formatPct(value: string | number | undefined): string {
  if (value == null || value === '') return '—'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(n)) return String(value)
  return `${n.toFixed(2)}%`
}

function formatOccupancy(value: string | number | undefined): string {
  if (value == null || value === '') return '—'
  return String(value)
}

function pctNumber(value: string | number | undefined): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'string' ? parseFloat(value) : value
  return Number.isNaN(n) ? null : n
}

export function PmcPerformancePage({ reportType }: PmcPerformanceRedPmcPerformancePageProps) {
  const { companyId } = useUserRole()
  const [params, setParams] = useState<PmcPerformanceRedListParams>(initialParams)
  const [rows, setRows] = useState<PmcPerformanceRow[]>([])
  const [salesTeams, setSalesTeams] = useState<SalesTeamOption[]>([])
  const [pageMeta, setPageMeta] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const reportLabel = reportType === 'green' ? 'Green' : 'Red'
  const testIdPrefix = `pmc-performance-${reportType}`

  useEffect(() => {
    void getSalesTeamsForPmcReport().then(setSalesTeams).catch(showApiError)
  }, [])

  useEffect(() => {
    if (companyId && !params.company_id) {
      setParams((p) => ({ ...p, company_id: String(companyId) }))
    }
  }, [companyId, params.company_id])

  const filterState: PmcPerformanceRedFilterState = useMemo(
    () => ({
      sales_team: params.sales_team,
      fromDt: params.fromDt,
      toDt: params.toDt,
      company_id: params.company_id,
      company_name: params.company_name,
      isRolling: params.isRolling,
    }),
    [params],
  )

  const exportUrl = useMemo(() => {
    const qs = buildPmcPerformanceExportParams({
      page: params.page,
      perPage: params.perPage,
      sortBy: params.sortBy,
      orderBy: params.orderBy,
      fromDt: params.fromDt,
      toDt: params.toDt,
      company_id: params.company_id,
      sales_team: params.sales_team,
      report_type: reportType,
    })
    return buildReportExportUrl('/api/export/pmc_performance', qs)
  }, [params, reportType])

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      appendPmcPerformanceListParams(qs, { ...params, report_type: reportType })
      writePmcPerformanceSession(buildPmcPerformanceSessionFromParams(params))
      const res = await getPmcPerformanceList(qs)
      setRows(res.data ?? [])
      setPageMeta({ current: res.current, last: res.last, total: res.total })
    } catch (e) {
      showApiError(e)
    } finally {
      setLoading(false)
    }
  }, [params, reportType])

  useEffect(() => {
    void fetchList()
  }, [fetchList])

  const handleRollingToggle = (
    checked: boolean,
  ): PmcPerformanceRedFilterState => {
    if (checked) {
      return { ...filterState, isRolling: true, ...getRolling52WeekRange() }
    }
    return {
      ...filterState,
      isRolling: false,
      fromDt: yearStart,
      toDt: formatDateForAPI(new Date()),
    }
  }

  const handleSort = (orderBy: PmcPerformanceRedSortKey) => {
    setParams((p) => ({
      ...p,
      page: 1,
      orderBy,
      sortBy: p.orderBy === orderBy && p.sortBy === 'desc' ? 'asc' : 'desc',
    }))
  }

  const columns = useMemo<
    ReportDataTableColumn<PmcPerformanceRow, PmcPerformanceRedSortKey>[]
  >(
    () => [
      {
        id: 'company_name',
        label: 'PMC',
        sortKey: 'company_name',
        render: (row) =>
          row.company_id ? (
            <Link
              to={`${ROUTE_PATTERNS.DASHBOARD_COMPANY_EDIT.replace(
                ':id',
                String(row.company_id),
              )}#statsinfo`}
              className="inline-flex items-center gap-1 text-[rgb(21,151,255)] underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.no ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[rgb(21,151,255)] px-1 text-[10px] font-bold leading-none text-white no-underline">
                  {row.no}
                </span>
              ) : null}
              {row.company_name ?? '—'}
            </Link>
          ) : (
            (row.company_name ?? '—')
          ),
      },
      {
        id: 'certificates',
        label: 'Contracts',
        sortKey: 'certificates',
        render: (row) => row.certificates ?? '—',
      },
      {
        id: 'change_certificates',
        label: '% Change',
        sortKey: 'change_certificates',
        render: (row) => {
          const pct = pctNumber(row.change_certificates)
          const emphasize =
            reportType === 'green'
              ? (pct ?? -Infinity) > -5
              : (pct ?? Infinity) <= -5
          return (
            <span
              className={cn({
                'font-semibold text-[#008000]':
                  reportType === 'green' && emphasize,
                'font-semibold text-[#ff0000]':
                  reportType === 'red' && emphasize,
              })}
            >
              {formatPct(row.change_certificates)}
            </span>
          )
        },
      },
      {
        id: 'delta_certificates',
        label: 'Δ',
        sortKey: 'delta_certificates',
        render: (row) => row.delta_certificates ?? '—',
      },
      {
        id: 'sales_exec',
        label: 'Sales Exec',
        sortKey: 'sales_exec',
        render: (row) => row.sales_exec ?? '—',
      },
      {
        id: 'manager_name',
        label: 'Acct Mgr',
        sortKey: 'manager_name',
        render: (row) => row.manager_name ?? '—',
      },
      {
        id: 'loss_ratio',
        label: 'Loss Ratio',
        sortKey: 'loss_ratio',
        render: (row) => formatPct(row.loss_ratio),
      },
      {
        id: 'occupancy',
        label: 'Occupancy',
        sortKey: 'occupancy',
        render: (row) => formatOccupancy(row.occupancy),
      },
    ],
    [reportType],
  )

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <PageShell
        title={`PMC Performance Report - ${reportLabel}`}
        pageHeaderTestIdRoot={`${testIdPrefix}-page-header`}
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          // Angular's PMC reports have no free-text search — filters only.
          hideSearch
          searchValue=""
          searchPlaceholder=""
          onSearchChange={() => {}}
          activeFilterCount={countActivePmcFilters(filterState)}
          onOpenFilters={() => setFiltersOpen(true)}
          actions={
            exportUrl ? (
              <a href={exportUrl} className={REPORT_EXPORT_BUTTON_CLASS}>
                <Download className="h-[18px] w-[18px]" />
                Export
              </a>
            ) : null
          }
        />

        <PmcPerformanceRedFilters
          filters={filterState}
          salesTeams={salesTeams}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) => setParams((p) => ({ ...p, ...next, page: 1 }))}
          onReset={() => {
            clearPmcPerformanceSession()
            setParams({
              ...DEFAULT_PARAMS,
              company_id: companyId ? String(companyId) : '',
            })
          }}
          onRollingToggle={handleRollingToggle}
        />

        <div className={reportStyles.tableCard}>
          <ListPaginationBar
            placement="header"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix={testIdPrefix}
          />

          <ReportDataTable
            rows={rows}
            columns={columns}
            loading={loading}
            orderBy={params.orderBy}
            sortBy={params.sortBy}
            emptyMessage="No records found"
            rowKey={(row, index) => row.company_id ?? index}
            onSort={handleSort}
            testIdPrefix={testIdPrefix}
          />

          <ListPaginationBar
            placement="footer"
            currentPage={pageMeta.current}
            lastPage={pageMeta.last}
            totalRecords={pageMeta.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            summaryLayout="angular"
            testIdPrefix={testIdPrefix}
          />
        </div>
      </PageShell>
    </div>
  )
}

export function PmcPerformanceRedPage() {
  return <PmcPerformancePage reportType="red" />
}
